import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  ImageList,
  ImageListItem,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../ui/PageHeader';

const totalGalleryImages = 24; // Update this based on how many images you have

const galleryImages = Array.from({ length: totalGalleryImages }).map((_, idx) => ({
  src: `/images/SJ_Gallery/showcase/gallery${idx + 1}.webp`, // e.g., gallery1.jpg, gallery2.jpg
  alt: `SJ Swimming Gallery Image ${idx + 1}`,
}));

// Motion Variants for staggering image load
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const Gallery = () => {
  const theme = useTheme();
  // Responsive columns
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));      // <600px → 1 col
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600–900px → 2 cols

  // Lightbox state
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const closeButtonRef = useRef(null);
  const touchStartX = useRef(null);

  let columns = 3;
  if (isMobile) columns = 1;
  else if (isTablet) columns = 2;

  const openLightbox = (index) => setSelectedImageIndex(index);
  const closeLightbox = useCallback(() => setSelectedImageIndex(null), []);

  const nextImage = useCallback(() => {
    setSelectedImageIndex((prev) => prev !== null ? (prev + 1) % galleryImages.length : null);
  }, []);

  const prevImage = useCallback(() => {
    setSelectedImageIndex((prev) => prev !== null ? (prev - 1 + galleryImages.length) % galleryImages.length : null);
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) nextImage();
    else prevImage();
  }, [nextImage, prevImage]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, closeLightbox, nextImage, prevImage]);

  // Prevent background scrolling when lightbox is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [selectedImageIndex]);

  useEffect(() => {
    if (selectedImageIndex !== null && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [selectedImageIndex]);

  return (
    <>
      <Container sx={{ mt: { xs: 4, sm: 8 }, mb: { xs: 6, sm: 10 } }}>
        <PageHeader
          title="Our Gallery"
          subtitle="A glimpse into our classes, squads, and special moments at SJ Swimming."
        />

        {/* Gallery Grid */}
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-50px' }}
        >
          <ImageList variant="masonry" cols={columns} gap={24}>
            {galleryImages.map((item, index) => (
              <ImageListItem
                key={index}
                component={motion.div}
                variants={itemVariants}
                role="button"
                tabIndex={0}
                aria-label={`View photo ${index + 1} of ${galleryImages.length}`}
                sx={{ overflow: 'hidden', borderRadius: 3, cursor: 'pointer' }}
                onClick={() => openLightbox(index)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(index); } }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)',
                    '&:hover img': {
                      transform: 'scale(1.04)',
                    },
                    '&:hover .overlay': {
                      opacity: 1,
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      display: 'block',
                      transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                  />
                  {/* Hover Overlay */}
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0) 100%)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        border: '1px solid rgba(255,255,255,0.15)'
                      }}
                    >
                      <Maximize2 size={24} />
                    </Box>
                  </Box>
                </Box>
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      </Container>

      {/* Full-Screen Immersive Lightbox */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeLightbox}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.92)',
              transform: 'translateZ(0)',
              willChange: 'opacity'
            }}
          >
            {/* Close */}
            <IconButton
              ref={closeButtonRef}
              aria-label="Close gallery"
              onClick={closeLightbox}
              sx={{
                position: 'absolute',
                top: { xs: 16, md: 32 },
                right: { xs: 16, md: 32 },
                color: 'white',
                background: 'rgba(0,0,0,0.6)',
                '&:hover': { background: 'rgba(0,0,0,0.85)' },
              }}
            >
              <X size={28} />
            </IconButton>

            {/* Prev Navigation */}
            <IconButton
              aria-label="Previous image"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              sx={{
                position: 'absolute',
                left: { xs: 12, md: 32 },
                color: 'white',
                background: 'rgba(0,0,0,0.6)',
                '&:hover': { background: 'rgba(0,0,0,0.85)' },
                zIndex: 2,
              }}
            >
              <ChevronLeft size={36} />
            </IconButton>

            {/* Next Navigation */}
            <IconButton
              aria-label="Next image"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              sx={{
                position: 'absolute',
                right: { xs: 12, md: 32 },
                color: 'white',
                background: 'rgba(0,0,0,0.6)',
                '&:hover': { background: 'rgba(0,0,0,0.85)' },
                zIndex: 2,
              }}
            >
              <ChevronRight size={36} />
            </IconButton>

            {/* Main Lightbox Image */}
            <Box
              component={motion.img}
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              src={galleryImages[selectedImageIndex].src}
              alt={galleryImages[selectedImageIndex].alt}
              sx={{
                maxHeight: '85vh',
                maxWidth: { xs: '80vw', md: '85vw' },
                borderRadius: 2,
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                objectFit: 'contain'
              }}
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking the image itself
            />
            
            {/* Image Counter */}
            <Typography
              sx={{
                position: 'absolute',
                bottom: { xs: 24, md: 40 },
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: 2,
                fontWeight: 500
              }}
            >
              {selectedImageIndex + 1} / {galleryImages.length}
            </Typography>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;